import React, { useState, useMemo } from 'react';
import { sortBy } from 'ramda';
import { RadioButton, ControlLink, Button, Stack, Text } from '@moda/om';
import { RestAddress } from '../../../hooks/useAddress';
import { Box } from '../../Box';
import { FormButtonsLayout } from '../../FormButtonsLayout';
import { ShippingAddressPreview } from '../ShippingAddressPreview';

import './ShippingAddressList.scss';

interface Props {
  addresses: RestAddress[];
  disabledAddressIds?: string[];
  defaultAddressId?: string;
  onAdd: () => void;
  onCancel: () => void;
  onChange: (addressId: string) => void;
  onEdit: (addressId: string) => void;
}

export const ShippingAddressList: React.FC<Props> = ({
  addresses,
  disabledAddressIds,
  defaultAddressId,
  onAdd,
  onCancel,
  onChange,
  onEdit
}) => {
  const [selectedAddressId, setSelectedAddressId] = useState<string | undefined>(defaultAddressId);

  const sortedAddresses = useMemo(
    () => sortBy(address => disabledAddressIds?.includes(address.id) ?? false, addresses),
    [addresses, disabledAddressIds]
  );

  return (
    <Stack className="ShippingAddressList" space={6}>
      <Stack space={4}>
        {sortedAddresses.map((address: RestAddress) => {
          const disabled = disabledAddressIds?.includes(address.id);

          return (
            <Box
              key={address.id}
              className="ShippingAddressList__option"
              tabIndex={0}
              role="button"
              onKeyPress={({ key }) =>
                key === 'Enter' && !disabled && setSelectedAddressId(address.id)
              }
              onClick={() => !disabled && setSelectedAddressId(address.id)}
            >
              <RadioButton
                disabled={disabled}
                checked={address.id === selectedAddressId}
                onChange={() => !disabled && setSelectedAddressId(address.id)}
                value={address.id}
              />
              <div className="ShippingAddressList__option-main">
                <Stack space={2}>
                  <ShippingAddressPreview attributes={address.attributes} />
                  {disabled && (
                    <Text color="code-red">
                      Some items can not be shipped to this address. Please edit or select another
                      address.
                    </Text>
                  )}
                </Stack>
                <ControlLink
                  className="ShippingAddressList__option-edit"
                  onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                    event.stopPropagation();
                    onEdit(address.id);
                  }}
                >
                  Edit
                </ControlLink>
              </div>
            </Box>
          );
        })}
      </Stack>

      <ControlLink className="ShippingAddressList__add-button" onClick={onAdd}>
        Add New Address
      </ControlLink>

      <FormButtonsLayout>
        <Button onClick={onCancel} secondary>
          Cancel
        </Button>

        {selectedAddressId && <Button onClick={() => onChange(selectedAddressId)}>Continue</Button>}
      </FormButtonsLayout>
    </Stack>
  );
};
